"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { mediaSrc } from "../../../_lib/api";
import type { BannerSlide, BlockLink, SiteBlock } from "../../../_lib/siteConfig";
import { useSite } from "../../../_lib/useSite";
import { Tooltip } from "../../../_ui/Tooltip";
import { IconChevron } from "../shell/parts";

/*
 * 블록 자리 — 관리자가 인트라넷 «화면 구성» 에서 넣은 블록을 그립니다 (`docs/base/12` §6-5).
 *
 * 🔴 **자리 밖에는 그리지 않습니다.** 화면은 `<Zone name="home.bottom" />` 한 줄로 자리를 두고, 무엇이
 *    들어갈지는 설정(봉투의 `blocks`)이 정합니다. 비어 있으면 **아무것도 그리지 않습니다**(빈 여백 없음).
 * 🔴 **모르는 종류는 정규화(`normalizeBlocks`)가 이미 버렸습니다** — 인트라넷이 먼저 배포되고 몰이 늦어도 깨지지 않습니다.
 * 🔴 **색·크기는 스킨 토큰만** — 블록 데이터에는 색 칸이 없습니다(`tone` 같은 이름 붙은 선택지뿐).
 * 🔴 **링크마다 툴팁** — 관리자가 넣은 문장입니다(서버가 필수로 받습니다). 외부 링크는 새 창.
 * 🔑 `data-block-id` 는 미리보기(`/preview`)가 «눌러서 고르기» 에 씁니다 — 운영 화면에서는 속성 하나일 뿐입니다.
 */
export function Zone({ name, className = "" }: { name: string; className?: string }) {
  const { blocks } = useSite();
  const list = blocks.filter((b) => b.zone === name);

  if (list.length === 0) return null;

  return (
    <div className={`flex flex-col gap-3 ${className}`} data-zone={name}>
      {list.map((block) => (
        <div key={block.id} data-block-id={block.id} className="rounded-[var(--tnf-radius-card)]">
          <Block block={block} />
        </div>
      ))}
    </div>
  );
}

function Block({ block }: { block: SiteBlock }) {
  switch (block.type) {
    case "banner":
      return <Banner slides={block.data.slides} ratio={block.data.ratio} />;
    case "notice":
      return <Notice {...block.data} />;
    case "text":
      return <RichText title={block.data.title} bodyHtml={block.data.bodyHtml} />;
    case "faq":
      return <Faq title={block.data.title} items={block.data.items} />;
  }
}

/** 링크 감싸기 — 없으면 그대로, 몰 안이면 `next/link`(전체 새로고침 없음 · 앱 래핑), 밖이면 새 창. */
function LinkWrap({ link, tooltip, external, className, wrap = "flex w-full", children }: BlockLink & {
  className?: string;
  /** 툴팁 틀의 클래스 — 배너처럼 높이를 채워야 하는 자리는 `h-full` 을 더합니다 */
  wrap?: string;
  children: React.ReactNode;
}) {
  if (!link) return <div className={className}>{children}</div>;

  return (
    <Tooltip text={tooltip ?? "열기"} className={wrap}>
      {(trigger) =>
        external ? (
          <a href={link} target="_blank" rel="noopener noreferrer" {...trigger} className={className}>
            {children}
            <span className="sr-only">(새 창)</span>
          </a>
        ) : (
          <Link href={link} {...trigger} className={className}>
            {children}
          </Link>
        )
      }
    </Tooltip>
  );
}

function Notice({ text, tone, ...link }: BlockLink & { text: string; tone: "plain" | "accent" }) {
  return (
    <LinkWrap
      {...link}
      className={`flex w-full items-center gap-2 rounded-[var(--tnf-radius-md)] px-4 py-3 text-body ${
        tone === "accent" ? "bg-[var(--tnf-accent-wash)] font-semibold text-ink" : "bg-surface text-ink-soft shadow-[var(--tnf-shadow-card)]"
      }`}
    >
      <span className="min-w-0 flex-1">{text}</span>
      {link.link && <IconChevron dir="right" size={16} className="shrink-0 text-ink-faint" />}
    </LinkWrap>
  );
}

function RichText({ title, bodyHtml }: { title: string | null; bodyHtml: string }) {
  return (
    <section className="rounded-[var(--tnf-radius-card)] bg-surface p-[18px] shadow-[var(--tnf-shadow-card)]">
      {title && <h2 className="mb-2.5 text-subtitle font-bold tracking-[-0.02em] text-ink">{title}</h2>}
      {/* 🔴 서버가 허용 목록으로 정화한 HTML 입니다(`HtmlSanitizer`) — 약관 시트와 같은 길. */}
      <div className="legal-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </section>
  );
}

function Faq({ title, items }: { title: string; items: { q: string; a: string }[] }) {
  return (
    <section className="rounded-[var(--tnf-radius-card)] bg-surface px-[18px] pt-[18px] pb-2 shadow-[var(--tnf-shadow-card)]">
      {title ? <h2 className="mb-1 text-subtitle font-bold tracking-[-0.02em] text-ink">{title}</h2> : <h2 className="sr-only">자주 묻는 질문</h2>}
      <div className="divide-y divide-[var(--tnf-line)]">
        {items.map((item, i) => (
          /* 🔑 `<details>` — 펼치고 접기가 스크립트 없이 되고, 키보드·낭독기가 그대로 압니다. */
          <details key={i} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-body font-semibold text-ink [&::-webkit-details-marker]:hidden">
              <span className="min-w-0 flex-1">{item.q}</span>
              <IconChevron size={18} className="shrink-0 text-ink-faint transition-transform group-open:rotate-180" />
            </summary>
            <p className="mt-2 text-body leading-relaxed whitespace-pre-line text-ink-soft">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

const RATIO_CLASS = { "3:1": "aspect-[3/1]", "2:1": "aspect-[2/1]", "1:1": "aspect-square" } as const;
const AUTO_ADVANCE_MS = 5000;

/*
 * 배너 — 한 장이면 그림 하나, 여러 장이면 옆으로 넘어갑니다.
 * 🔑 `default` 의 `BannerStrip` 과 같은 원칙입니다 — 5초 자동 넘김, 마우스가 올라가 있거나 초점이 안에 있거나
 *    «동작 줄이기» 면 멈춤, 페이징·정지 버튼 없음(스와이프). 끝에서는 처음으로 돌아갑니다.
 * 🔴 첫 장만 `eager` — 나머지는 넘길 때 받습니다(모바일 데이터로 첫 화면을 여는 사용자층).
 */
function Banner({ slides, ratio }: { slides: BannerSlide[]; ratio: "3:1" | "2:1" | "1:1" }) {
  const track = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const many = slides.length > 1;

  useEffect(() => {
    if (!many || paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      const el = track.current;
      if (!el || el.clientWidth === 0) return;
      const next = Math.round(el.scrollLeft / el.clientWidth) + 1;
      el.scrollTo({ left: next >= slides.length ? 0 : next * el.clientWidth, behavior: "smooth" });
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [many, paused, slides.length]);

  return (
    <section
      aria-label="안내 배너"
      className="overflow-hidden rounded-[var(--tnf-radius-card)] bg-surface shadow-[var(--tnf-shadow-card)]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div
        ref={track}
        className={`flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${RATIO_CLASS[ratio]}`}
        role={many ? "group" : undefined}
        aria-label={many ? `배너 ${slides.length}장 — 옆으로 넘겨 봅니다` : undefined}
      >
        {slides.map((slide, i) => (
          <div key={i} className="h-full w-full shrink-0 snap-center">
            <LinkWrap {...slide} wrap="flex h-full w-full" className="block h-full w-full">
              {/* eslint-disable-next-line @next/next/no-img-element -- 관리자 업로드 이미지(BFF 경유) */}
              <img
                src={mediaSrc(slide.imageUrl) ?? ""}
                alt={slide.alt}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                className="h-full w-full object-cover"
              />
            </LinkWrap>
          </div>
        ))}
      </div>
    </section>
  );
}
